import type { Snapshot } from 'recoil'
import { additionalFamilyDraftAtom, caseValuesAtom } from 'atoms'
import { formFieldKeys, newObjects } from '@messenger/consts'
import {
  additionalFamilyTypes,
  applyAdditionalFamilyDraft,
  type AdditionalFamilyDraft,
} from '@messenger/utils'

type FamilyEntry = {
  name: string
  relation: string | null
  type: string
  [key: string]: unknown
}

type CaseFieldValue = { id?: string; value?: FamilyEntry[] } | undefined

type RecoilFlushContext = {
  snapshot: Snapshot
  // Recoil's CallbackInterface set — typed loosely because the atoms are
  // declared in an untyped legacy module.
  set: (recoilState: unknown, value: unknown) => void
}

/**
 * Flush a pending additional-family draft (typed into the flat input but
 * not yet "Add"-ed) into the additional_family atom so a plain Save
 * persists it. Returns the flushed entries array, or null when there was
 * nothing pending — callers must override their (pre-flush snapshot)
 * payload with the returned array, since the Recoil snapshot they iterate
 * was taken before this set.
 */
export const flushAdditionalFamilyDraft = async ({
  snapshot,
  set,
}: RecoilFlushContext): Promise<FamilyEntry[] | null> => {
  const draft = (await snapshot.getPromise(
    additionalFamilyDraftAtom,
  )) as AdditionalFamilyDraft

  const fieldValue = (await snapshot.getPromise(
    caseValuesAtom(formFieldKeys.additionalFamily),
  )) as CaseFieldValue
  const entries = fieldValue?.value ?? []

  const updated = applyAdditionalFamilyDraft(
    entries,
    draft,
    (name, relation): FamilyEntry => ({
      ...newObjects.additionalFamily,
      name,
      relation,
      type: additionalFamilyTypes.additionalFamily,
    }),
  )
  // Blank/invisible-only drafts (and no draft at all) flush nothing.
  if (updated === entries) return null

  set(caseValuesAtom(formFieldKeys.additionalFamily), {
    ...fieldValue,
    id: formFieldKeys.additionalFamily,
    value: updated,
  })
  set(additionalFamilyDraftAtom, null)

  return updated
}

export default flushAdditionalFamilyDraft
